export type ImageDimensions = { width: number; height: number };

const u16be = (b: Uint8Array, i: number) =>
  ((b[i] ?? 0) << 8) | (b[i + 1] ?? 0);
const u16le = (b: Uint8Array, i: number) =>
  (b[i] ?? 0) | ((b[i + 1] ?? 0) << 8);
const u24le = (b: Uint8Array, i: number) =>
  (b[i] ?? 0) | ((b[i + 1] ?? 0) << 8) | ((b[i + 2] ?? 0) << 16);
const u32be = (b: Uint8Array, i: number) =>
  ((b[i] ?? 0) << 24) |
  ((b[i + 1] ?? 0) << 16) |
  ((b[i + 2] ?? 0) << 8) |
  (b[i + 3] ?? 0);

function ascii(bytes: Uint8Array, offset: number, length: number) {
  let out = "";
  for (let i = 0; i < length; i++)
    out += String.fromCharCode(bytes[offset + i] ?? 0);
  return out;
}

function startsWith(bytes: Uint8Array, signature: readonly number[]) {
  return (
    bytes.length >= signature.length &&
    signature.every((byte, i) => bytes[i] === byte)
  );
}

function pngDimensions(bytes: Uint8Array): ImageDimensions | null {
  if (bytes.length < 24 || ascii(bytes, 12, 4) !== "IHDR") return null;
  return { width: u32be(bytes, 16), height: u32be(bytes, 20) };
}

function gifDimensions(bytes: Uint8Array): ImageDimensions | null {
  if (bytes.length < 10) return null;
  return { width: u16le(bytes, 6), height: u16le(bytes, 8) };
}

function webpDimensions(bytes: Uint8Array): ImageDimensions | null {
  if (bytes.length < 30 || ascii(bytes, 8, 4) !== "WEBP") return null;
  switch (ascii(bytes, 12, 4)) {
    case "VP8 ":
      return {
        width: u16le(bytes, 26) & 0x3fff,
        height: u16le(bytes, 28) & 0x3fff,
      };
    case "VP8L": {
      const packed =
        ((bytes[21] ?? 0) |
          ((bytes[22] ?? 0) << 8) |
          ((bytes[23] ?? 0) << 16) |
          ((bytes[24] ?? 0) << 24)) >>>
        0;
      return {
        width: (packed & 0x3fff) + 1,
        height: ((packed >>> 14) & 0x3fff) + 1,
      };
    }
    case "VP8X":
      return { width: u24le(bytes, 24) + 1, height: u24le(bytes, 27) + 1 };
    default:
      return null;
  }
}

// 0xC4/0xC8/0xCC are DHT/JPG/DAC, which share the 0xCn range but carry no frame header.
const isStartOfFrame = (marker: number) =>
  marker >= 0xc0 &&
  marker <= 0xcf &&
  marker !== 0xc4 &&
  marker !== 0xc8 &&
  marker !== 0xcc;

function jpegDimensions(bytes: Uint8Array): ImageDimensions | null {
  let i = 2;
  while (i + 9 < bytes.length) {
    if (bytes[i] !== 0xff) {
      i++;
      continue;
    }
    const marker = bytes[i + 1] ?? 0;
    if (marker === 0xff) {
      i++;
      continue;
    }
    if (
      marker === 0xd8 ||
      marker === 0x01 ||
      (marker >= 0xd0 && marker <= 0xd7)
    ) {
      i += 2;
      continue;
    }
    if (marker === 0xda) return null;
    if (isStartOfFrame(marker)) {
      return { width: u16be(bytes, i + 7), height: u16be(bytes, i + 5) };
    }
    const segmentLength = u16be(bytes, i + 2);
    if (segmentLength < 2) return null;
    i += 2 + segmentLength;
  }
  return null;
}

/**
 * Reads pixel dimensions from a container header without decoding the image, so
 * callers can size a decode target before allocating a full-resolution bitmap.
 * Returns null for anything it cannot read, including truncated prefixes.
 */
export function imageDimensions(bytes: Uint8Array): ImageDimensions | null {
  const dimensions = startsWith(bytes, [0x89, 0x50, 0x4e, 0x47])
    ? pngDimensions(bytes)
    : startsWith(bytes, [0xff, 0xd8])
      ? jpegDimensions(bytes)
      : startsWith(bytes, [0x47, 0x49, 0x46])
        ? gifDimensions(bytes)
        : startsWith(bytes, [0x52, 0x49, 0x46, 0x46])
          ? webpDimensions(bytes)
          : null;
  if (!dimensions) return null;
  return dimensions.width > 0 && dimensions.height > 0 ? dimensions : null;
}
